import { ApplicationRef, inject, Injectable, NgZone } from '@angular/core'
import { SwUpdate, UnrecoverableStateEvent, VersionEvent } from '@angular/service-worker'
import { BehaviorSubject, interval } from 'rxjs'
import { first, map, switchMap } from 'rxjs/operators'

import { environment } from 'environments/environment'

import { clearCache } from '@app/components/generic/utilities'

import { NetworkService } from './network.service'

@Injectable({ providedIn: 'root' })
export class UpdateService {
  private readonly appRef = inject(ApplicationRef)
  private readonly updates = inject(SwUpdate)
  private readonly networkService = inject(NetworkService)
  private readonly zone = inject(NgZone)

  private _updateAvailable = new BehaviorSubject<boolean>(false)
  private readonly CHECK_UPDATE_TIMEOUT_MS: number = 10000

  constructor () {
    console.debug('updates.isEnabled')
    console.debug(this.updates.isEnabled)
    console.debug('environment')
    console.debug(environment)

    if (!this.updates.isEnabled) {
      console.debug('ngsw updates are disabled!')
      return
    }

    // Listen to new update available events
    this.updates.versionUpdates.subscribe(this.emitUpdateAvailableWhenVersionUpdateIsReady.bind(this))

    // In case the application ends up in an 'unrecoverable state', e.g. there are javascript chunks missing,
    // force a page refresh to reload from scratch
    // https://angular.io/guide/service-worker-communications#handling-an-unrecoverable-state
    this.updates.unrecoverable.subscribe(this.reloadWindow)

    this.zone.runOutsideAngular(() => {
      // Check for updates every `checkUpdatesInterval` milliseconds
      if (environment.checkUpdatesInterval) {
        this.appRef.isStable.pipe(
          first(stable => stable),
          switchMap(() => interval(environment.checkUpdatesInterval).pipe(
            map(() => {
              console.debug('Checking for updates…')
              Promise
                .race([this.checkForUpdate(), this.timeout(this.CHECK_UPDATE_TIMEOUT_MS)])
                .catch((reason: Error) => {
                  // Reload app on update timeout while online.
                  if (reason.message === 'timeout' && this.networkService.online$) {
                    // this.errorHandler.handleError(reason)
                    // window.location.reload()
                    console.error('timeout on service worker')
                  }
                })
            }),
          )),
        ).subscribe()
      }
    })
  }

  private timeout (timeout: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        reject(new Error('timeout'))
      }, timeout)
    })
  }

  private checkForUpdate (): Promise<void> {
    return this.updates.checkForUpdate().then((result: boolean) => {
      console.debug('update is available: ' + result)
    }, (reason: any) => {
      console.warn('update was rejected:')
      console.warn(reason)
    }).catch((reason: any) => {
      console.error('error during update..')
      console.error(reason)
    })
  }

  private reloadWindow (event: UnrecoverableStateEvent) {
    console.warn('detected unrecoverable service worker error, reloading')
    console.warn(event)
    window.location.reload()
  }

  private emitUpdateAvailableWhenVersionUpdateIsReady (event: VersionEvent) {
    console.debug('got event..')
    console.debug(event)
    if (event.type === 'VERSION_READY') {
      console.log(`New update available ${event.currentVersion.hash} ⇒ ${event.latestVersion.hash}`)
      this._updateAvailable.next(true)
    }
  }

  public getUpdateAvailable () {
    return this._updateAvailable.asObservable()
  }

  public update () {
    this.updates.activateUpdate()
      .then(() => clearCache())
      .then(() => document.location.reload())
  }
}
